import { format } from 'date-fns'

import type { EAvailablePlatform, ECurrency, IShowCatalogProduct } from '@pkg/lib-data'
import { CURRENCY_SYMBOLS, EPassengerType, EUserJourneyType, resolveShowResourceLevel } from '@pkg/lib-data'

import { DATE_STRING_FORMAT, defineImages, formatOptions, generateImageAlt } from '../utils'

export interface ProductDetailsDataShowsArgs {
  product: IShowCatalogProduct
  id: string
  eventDate: Date
  categoryId: string
  getAllowedPlatforms: () => EAvailablePlatform[]
}

export interface ProductDetailsDataShows {
  type: EUserJourneyType.SHOWS
  categoryId: string
  api: IShowCatalogProduct
  computed: {
    id: string
    imageUrls: any[] | null
    resourceLevel: number
    currencyIso: string
    currencySymbol: string
    dateLabelPrimary: string
    eventDateFormatted: string
    dateLabelSecondary: string
    comment: string
    seatingComment: string
    locationLabel: string
    title: string
    showName: string
    youtubeVideoId?: string
    dealTicker: string
    placeName: string
    supplierId: string
    eventDate: Date | string
    pricesMap: Record<EPassengerType, number>
    isPromoted: boolean
    dealType?: string
    eventCategory: string
    soldOut: boolean
    fromDateRelatedGroup: string
    detailsShow: string
  }
  filters: {
    price: number
    category: { label: string; name: string }
    areas: { name: string; label: string }
    showName: { label: string; name: string }
    dates: Record<string, string[]>
  }
}

// Simplified platform check - for widget, allow all platforms
const checkIsSupportedPlatform = (
  _product: IShowCatalogProduct,
  _getAllowedPlatforms: () => EAvailablePlatform[],
): boolean => {
  return true
}

export const defineProductDetailsDataShows = (args: ProductDetailsDataShowsArgs): ProductDetailsDataShows | null => {
  const { product, id, eventDate, categoryId, getAllowedPlatforms } = args

  const isSupportedPlatform = checkIsSupportedPlatform(product, getAllowedPlatforms)

  if (!isSupportedPlatform) return null

  const dateLabelPrimary = `${format(eventDate, 'EEEE', formatOptions)} ${format(eventDate, DATE_STRING_FORMAT, formatOptions)}`
  const eventDateFormatted = `${format(eventDate, 'dd.MM.yy', formatOptions)} ${format(eventDate, 'EEEEE', formatOptions)}`

  const productDetailsData: ProductDetailsDataShows = {
    type: EUserJourneyType.SHOWS,
    categoryId,
    api: product,
    computed: {
      id,
      imageUrls: defineImages({
        imageUrls: [
          {
            imageUrl: product.imageUrl,
            alt: product.imageAlt || generateImageAlt(product.name || '', product.areaName || ''),
          },
        ],
      }),
      resourceLevel: resolveShowResourceLevel(product),
      currencyIso: product.currencyIso,
      currencySymbol: CURRENCY_SYMBOLS[product.currencyIso as ECurrency],
      dateLabelPrimary,
      eventDateFormatted,
      dateLabelSecondary: product.time,
      comment: product.comment || '',
      seatingComment: product.supplierRemark || '',
      locationLabel: product.address,
      title: product.name || '',
      showName: product.name || '',
      youtubeVideoId: extractYoutubeVideoId(product.youtubeUrl),
      dealTicker: product.dealTicker || '',
      placeName: product.placeName,
      supplierId: product.supplierId,
      eventDate,
      detailsShow: product.description,
      soldOut: product.soldOut || false,
      fromDateRelatedGroup: '',
      pricesMap: {
        [EPassengerType.ADULT]: Math.trunc(product.sellingPrice),
        [EPassengerType.INFANT]: 0,
        [EPassengerType.CHILD]: 0,
        [EPassengerType.SENIOR]: 0,
      },
      isPromoted: product.isPromoted,
      dealType: product.dealType,
      eventCategory: product.category,
    },
    filters: {
      price: Math.trunc(product.sellingPrice),
      category: {
        label: product.category,
        name: product.category,
      },
      areas: { name: product.areaName, label: product.areaName },
      showName: { label: product.name || '', name: product.name },
      dates: { [format(eventDate, 'yyyy-MM-dd')]: [] },
    },
  }

  return productDetailsData
}

const extractYoutubeVideoId = (youtubeUrl?: string) => {
  try {
    if (!youtubeUrl) return undefined

    const url = new URL(youtubeUrl)
    const pathname = url.pathname

    const videoId = url.searchParams.get('v') || pathname.split('/shorts/')[1] || undefined

    return videoId
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error)

    return undefined
  }
}
